import React from 'react';
import Icon from 'components/AppIcon';

const GOAL_OPTIONS = [
  {
    id: 'vacation',
    label: 'Vacation Savings',
    icon: 'Plane',
    description: 'Save for your dream trip',
    color: '#06B6D4',
    defaultTarget: 3000,
    defaultMonths: 12,
  },
  {
    id: 'emergency',
    label: 'Emergency Fund',
    icon: 'Shield',
    description: '3-6 months of expenses',
    color: '#10B981',
    defaultTarget: 10000,
    defaultMonths: 18,
  },
  {
    id: 'debt',
    label: 'Debt Repayment',
    icon: 'CreditCard',
    description: 'Pay off loans & credit cards',
    color: '#EF4444',
    defaultTarget: 5000,
    defaultMonths: 24,
  },
  {
    id: 'home',
    label: 'Home Down Payment',
    icon: 'Home',
    description: 'Save for your first home',
    color: '#8B5CF6',
    defaultTarget: 50000,
    defaultMonths: 60,
  },
  {
    id: 'retirement',
    label: 'Retirement',
    icon: 'Sunset',
    description: 'Build long-term wealth',
    color: '#F59E0B',
    defaultTarget: 100000,
    defaultMonths: 120,
  },
  {
    id: 'education',
    label: 'Education Fund',
    icon: 'GraduationCap',
    description: 'Invest in learning',
    color: '#EC4899',
    defaultTarget: 15000,
    defaultMonths: 36,
  },
];

export default function GoalsStep({ data, onChange }) {
  const selectedGoals = data?.selectedGoals || [];
  const goalDetails = data?.goalDetails || {};

  const toggleGoal = (goalId) => {
    const updated = selectedGoals?.includes(goalId)
      ? selectedGoals?.filter((g) => g !== goalId)
      : [...selectedGoals, goalId];
    onChange({ ...data, selectedGoals: updated });
  };

  const updateGoalDetail = (goalId, field, value) => {
    onChange({
      ...data,
      goalDetails: {
        ...goalDetails,
        [goalId]: { ...(goalDetails?.[goalId] || {}), [field]: value },
      },
    });
  };

  const getGoalDetail = (goalId, field, defaultVal) => {
    return goalDetails?.[goalId]?.[field] ?? defaultVal;
  };

  return (
    <div className="space-y-5">
      <div>
        <h3 className="text-base font-semibold text-[#F8FAFC] mb-1">Financial Goals</h3>
        <p className="text-sm text-[#94A3B8] mb-4">
          Select your financial goals. We'll create a personalized savings plan for each.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {GOAL_OPTIONS?.map((goal) => {
          const isSelected = selectedGoals?.includes(goal?.id);
          return (
            <div key={goal?.id} className="space-y-2">
              <button
                onClick={() => toggleGoal(goal?.id)}
                className={`w-full flex items-center gap-3 p-4 rounded-lg border transition-all duration-200 text-left min-h-[44px] ${
                  isSelected
                    ? 'border-[rgba(6,182,212,0.4)] bg-[rgba(6,182,212,0.08)]'
                    : 'border-[rgba(6,182,212,0.12)] bg-[rgba(15,23,42,0.5)] hover:border-[rgba(6,182,212,0.25)]'
                }`}
              >
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: `${goal?.color}20` }}
                >
                  <Icon name={goal?.icon} size={20} color={goal?.color} strokeWidth={2} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-semibold ${isSelected ? 'text-[#F8FAFC]' : 'text-[#CBD5E1]'}`}>
                    {goal?.label}
                  </p>
                  <p className="text-xs text-[#94A3B8]">{goal?.description}</p>
                </div>
                <div
                  className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                    isSelected ? 'bg-[#06B6D4] border-[#06B6D4]' : 'border-[rgba(6,182,212,0.3)]'
                  }`}
                >
                  {isSelected && <Icon name="Check" size={12} color="#0F172A" strokeWidth={3} />}
                </div>
              </button>
              {isSelected && (
                <div className="ml-4 p-3 rounded-lg bg-[rgba(6,182,212,0.05)] border border-[rgba(6,182,212,0.1)] grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-[#94A3B8] mb-1">Target Amount ($)</label>
                    <div className="relative">
                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[#94A3B8] text-xs">$</span>
                      <input
                        type="number"
                        min="0"
                        value={getGoalDetail(goal?.id, 'target', goal?.defaultTarget)}
                        onChange={(e) => updateGoalDetail(goal?.id, 'target', e?.target?.value)}
                        className="w-full pl-5 pr-2 py-1.5 bg-[rgba(6,182,212,0.06)] border border-[rgba(6,182,212,0.15)] rounded text-xs text-[#F8FAFC] focus:outline-none focus:border-[#06B6D4] transition-all"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs text-[#94A3B8] mb-1">Timeline (months)</label>
                    <input
                      type="number"
                      min="1"
                      max="360"
                      value={getGoalDetail(goal?.id, 'months', goal?.defaultMonths)}
                      onChange={(e) => updateGoalDetail(goal?.id, 'months', e?.target?.value)}
                      className="w-full px-2 py-1.5 bg-[rgba(6,182,212,0.06)] border border-[rgba(6,182,212,0.15)] rounded text-xs text-[#F8FAFC] focus:outline-none focus:border-[#06B6D4] transition-all"
                    />
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {selectedGoals?.length === 0 && (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-[rgba(245,158,11,0.08)] border border-[rgba(245,158,11,0.2)]">
          <Icon name="AlertCircle" size={16} color="#F59E0B" />
          <p className="text-xs text-[#F59E0B]">Please select at least one financial goal to continue.</p>
        </div>
      )}
    </div>
  );
}